// Record all canvas calls when rendering so that we can replay the calls without
// needing to do do all of the computation for layout again.

// Information about a single call to the canvas.
type CallRecord =
  | {
      type: 'set';
      name: string;
      value: any;
    }
  | {
      type: 'call';
      name: string;
      args: any[];
      additionalData?: any;
    };

// Check if a canvas context is tainted.
function isTainted(ctx) {
  try {
    ctx.getImageData(0, 0, 1, 1);
    return false;
  } catch (err) {
    return err.code === 18;
  }
}

// Class to encapsulate rendering behaviors for a whiteboard. Currently it
// only performs recording, but future versions might also implement WebGL
// rendering.
class RecordingRenderManager {
  // Cache of recorded calls for each object.
  recordingCache: Map<string, CallRecord[]>;

  constructor() {
    this.recordingCache = new Map<string, CallRecord[]>();
  }

  // Completely clear the recording cache.
  refreshAll() {
    this.recordingCache.clear();
  }

  // Remove the cached calls for an object so it re-records during the next
  // render.
  refreshObject(object: fabric.Object) {
    this.recordingCache.delete(object.id);
  }

  // Record all calls to the canvas context while running a function. On future
  // calls only reply the recorded calls without actually calling `func`.
  withRecordedHTMLCanvas(
    object: fabric.Object,
    ctx: CanvasRenderingContext2D,
    func: (ctx: CanvasRenderingContext2D) => void
  ) {
    // When exporting to SVG don't use the cache. It's not necessary and
    // the recorded calls use a real context, so can't be replayed on an SVG.
    if (object.canvas.exporting) {
      func(ctx);
      return;
    }

    // Opt out based on object settings
    if (object.skipRenderRecording) {
      func(ctx);
      return;
    }

    const calls = this.recordingCache.get(object.id);
    if (calls) {
      this.replayHTMLCanvas(ctx, calls);
    } else {
      const calls: CallRecord[] = [];
      // We add to the cache before rendering so that the render process can
      // itself remove this object from the cache if it needs to.
      this.recordingCache.set(object.id, calls);

      const proxiedCtx = new Proxy(ctx, {
        get(target, prop) {
          if (typeof target[prop] === 'function') {
            return function (...args) {
              let additionalData;

              if (prop === 'fillText') {
                // Save enough data to be able to greek the text at small sizes.
                const textMetrics = ctx.measureText(args[0]);
                additionalData = {
                  width: textMetrics.width,
                  height: Math.max(
                    textMetrics.fontBoundingBoxAscent,
                    textMetrics.fontBoundingBoxDescent
                  ),
                };
              }

              if (prop === 'drawImage') {
                // Generate a smaller version of  the image. This is helpful
                // because drawing images is expensive and we use a huge number
                // of them for text rendering.
                additionalData = { lowResImage: null };
                RecordingRenderManager.generateLowResImage(args[0]).then(
                  result => {
                    additionalData.lowResImage = result;
                  }
                );
              }

              calls.push({
                type: 'call',
                name: String(prop),
                args,
                additionalData,
              });
              return target[prop](...args);
            };
          } else {
            return Reflect.get(target, prop);
          }
        },
        set(target, prop, value) {
          calls.push({ type: 'set', name: String(prop), value });
          return Reflect.set(target, prop, value);
        },
      });

      // Call the function that does rendering we will record.
      func(proxiedCtx);
    }
  }

  // Replay an array of calls on a canvas context.
  private replayHTMLCanvas(ctx: CanvasRenderingContext2D, calls: CallRecord[]) {
    for (const call of calls) {
      if (call.type === 'set') {
        ctx[call.name] = call.value;
      } else {
        if (!call.args) continue;

        // No need to measure text since the drawing is already computed.
        if (call.name === 'measureText') continue;

        // Potentially greek text if it's too small.
        if (call.name === 'fillText') {
          const matrix = ctx.getTransform();
          const scale = Math.sqrt(matrix.a * matrix.a + matrix.b * matrix.b);

          if (scale * call.additionalData.height < 6) {
            let x = call.args[1] || 0;
            let y = call.args[2] || 0;

            switch (ctx.textBaseline) {
              case 'bottom':
                y -= call.additionalData.height;
                break;
              case 'middle':
                y -= call.additionalData.height / 2;
                break;
            }
            switch (ctx.textAlign) {
              case 'center':
                x -= call.additionalData.width / 2;
                break;
              case 'right':
                x -= call.additionalData.width;
                break;
            }

            // We need to lighten the greeked text a lot, otherwise it is too dark.
            // It renders using the fill color that was already set to render the
            // text.
            const oldAlpha = ctx.globalAlpha;
            ctx.globalAlpha = oldAlpha * 0.3;
            ctx.fillRect(
              x,
              y,
              call.additionalData.width,
              call.additionalData.height
            );
            ctx.globalAlpha = oldAlpha;

            continue;
          }
        }

        // Use low res version when images are small. This only works when the
        // width and height are specified.
        if (call.name === 'drawImage' && call.args.length > 3) {
          const matrix = ctx.getTransform();
          const scale = Math.sqrt(matrix.a * matrix.a + matrix.b * matrix.b);
          const width = call.args[2];
          const height = call.args[3];
          if (
            call.additionalData.lowResImage &&
            scale < 0.1 &&
            scale * width < 40 &&
            scale * height < 40
          ) {
            ctx.drawImage(
              call.additionalData.lowResImage,
              // @ts-expect-error We are passing the arguments we previously saved.
              ...call.args.slice(1)
            );
            continue;
          }
        } else if (call.name === 'drawImage' && call.args.length === 3) {
          // If the width and height are not specified, we can't use the low res
          // version. Fix the code that called drawImage with only 3 args.
          console.log(
            'drawImage could be optimized if width and height were specified'
          );
        }

        ctx[call.name](...call.args);
      }
    }
  }

  // ensure we only generate the low res version of an image once
  static imageMap = new WeakMap<HTMLImageElement, HTMLImageElement>();

  static async generateLowResImage(image: HTMLImageElement) {
    const scale = 0.1;

    if (this.imageMap.has(image)) {
      return this.imageMap.get(image);
    } else {
      return await new Promise(resolve => {
        // Create an off-screen canvas
        const canvas = document.createElement('canvas');
        canvas.width = image.width * scale;
        canvas.height = image.height * scale;

        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);

        // Draw the scaled image
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        if (isTainted(ctx)) {
          return resolve(null);
        }
        const newImage = new Image();
        newImage.src = canvas.toDataURL();
        newImage
          .decode()
          .then(() => {
            // NOTE there is a potential race condition where multiple calls generate the same image in parallel.
            // That's not a major issue though
            this.imageMap.set(image, newImage);
            resolve(newImage);
          })
          .catch(error => {
            console.error(`Failed to create low res image: `, error);
            resolve(null);
          });
      });
    }
  }
}

export default RecordingRenderManager;
